import { useState, useEffect } from 'react'
import { Table, Card, Button, Modal, Form, Input, InputNumber, Switch, message, Popconfirm, Space, Tag } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { memberApi } from '../services/api'

interface MemberLevel {
  id: number
  name: string
  min_points: number
  discount: number
  points_rate: number
  icon: string
  color: string
  benefits: string
  status: number
}

const MemberLevelPage: React.FC = () => {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<MemberLevel[]>([])
  const [modalVisible, setModalVisible] = useState(false)
  const [editing, setEditing] = useState<MemberLevel | null>(null)
  const [form] = Form.useForm()

  useEffect(() => {
    fetchLevels()
  }, [])

  const fetchLevels = async () => {
    setLoading(true)
    try {
      const res: any = await memberApi.getLevels()
      if (res.code === 200) setData(res.data || [])
    } catch (err) {
      message.error('获取会员等级失败')
    }
    setLoading(false)
  }

  const handleAdd = () => {
    setEditing(null)
    form.resetFields()
    setModalVisible(true)
  }

  const handleEdit = (record: MemberLevel) => {
    setEditing(record)
    form.setFieldsValue(record)
    setModalVisible(true)
  }

  const handleDelete = async (id: number) => {
    try {
      await memberApi.deleteLevel(id)
      message.success('删除成功')
      fetchLevels()
    } catch (err) {
      message.error('删除失败')
    }
  }

  const handleSave = async () => {
    try {
      const values = await form.validateFields()
      if (editing) {
        await memberApi.updateLevel(editing.id, values)
      } else {
        await memberApi.createLevel(values)
      }
      message.success('保存成功')
      setModalVisible(false)
      fetchLevels()
    } catch (err) {
      message.error('保存失败')
    }
  }

  const columns = [
    {
      title: '等级名称',
      dataIndex: 'name',
      render: (name: string, record: MemberLevel) => (
        <Tag color={record.color || '#55266c'}>{name}</Tag>
      )
    },
    {
      title: '所需积分',
      dataIndex: 'min_points'
    },
    {
      title: '折扣',
      dataIndex: 'discount',
      render: (v: number) => `${Math.round(v * 100)}%`
    },
    {
      title: '积分倍率',
      dataIndex: 'points_rate',
      render: (v: number) => `${v}x`
    },
    {
      title: '状态',
      dataIndex: 'status',
      render: (status: number) => (
        <Tag color={status === 1 ? 'green' : 'default'}>
          {status === 1 ? '启用' : '停用'}
        </Tag>
      )
    },
    {
      title: '操作',
      render: (_: any, record: MemberLevel) => (
        <Space>
          <Button type="link" size="small" onClick={() => handleEdit(record)}>编辑</Button>
          <Popconfirm title="确定删除该等级吗？" onConfirm={() => handleDelete(record.id)}>
            <Button type="link" size="small" danger>删除</Button>
          </Popconfirm>
        </Space>
      )
    }
  ]

  return (
    <Card
      title="会员等级管理"
      extra={<Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>新增等级</Button>}
    >
      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={false}
      />

      <Modal
        title={editing ? '编辑等级' : '新增等级'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => setModalVisible(false)}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="等级名称" rules={[{ required: true }]}>
            <Input placeholder="如：黄金会员" />
          </Form.Item>
          <Form.Item name="min_points" label="所需积分" rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="discount" label="折扣系数" extra="0.95表示95折">
            <InputNumber min={0.5} max={1} step={0.01} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="points_rate" label="积分倍率" extra="消费1元获得的积分倍数">
            <InputNumber min={1} max={10} step={0.5} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="color" label="等级颜色">
            <Input placeholder="如：#FFD700" />
          </Form.Item>
          <Form.Item name="icon" label="等级图标">
            <Input placeholder="图标URL" />
          </Form.Item>
          <Form.Item name="benefits" label="等级权益">
            <Input.TextArea rows={3} placeholder="JSON格式权益说明" />
          </Form.Item>
          <Form.Item name="status" label="状态" valuePropName="checked" initialValue={true}>
            <Switch checkedChildren="启用" unCheckedChildren="停用" 
              onChange={(v) => form.setFieldValue('status', v ? 1 : 0)} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  )
}

export default MemberLevelPage
